import type { CacheSliceConfig, CacheSliceState } from './types'

export type CacheStore = {
  getSlice: <T>(key: string) => CacheSliceState<T>
  setSlice: <T>(key: string, state: CacheSliceState<T>) => void
  subscribe: (key: string, callback: () => void) => () => void
  getSnapshot: <T>(key: string) => CacheSliceState<T>
}

export function createCacheStore(
  sliceConfigs: Record<string, CacheSliceConfig<any>>,
): CacheStore {
  const slices = new Map<string, CacheSliceState<any>>()
  const snapshots = new Map<string, CacheSliceState<any>>()
  const listeners = new Map<string, Set<() => void>>()

  // Initialize each slice with its initial value
  for (const [key, config] of Object.entries(sliceConfigs)) {
    const initial: CacheSliceState<any> = {
      data: config.initialValue,
      isLoading: true,
      error: null,
      lastFetchedAt: 0,
    }
    slices.set(key, initial)
    snapshots.set(key, initial)
  }

  function getSlice<T>(key: string): CacheSliceState<T> {
    return slices.get(key)!
  }

  function setSlice<T>(key: string, state: CacheSliceState<T>): void {
    slices.set(key, state)
    // Replace snapshot reference so useSyncExternalStore detects the change
    snapshots.set(key, state)
    const keyListeners = listeners.get(key)
    if (keyListeners) {
      keyListeners.forEach((fn) => fn())
    }
  }

  function subscribe(key: string, callback: () => void): () => void {
    if (!listeners.has(key)) {
      listeners.set(key, new Set())
    }
    listeners.get(key)!.add(callback)
    return () => {
      listeners.get(key)!.delete(callback)
    }
  }

  function getSnapshot<T>(key: string): CacheSliceState<T> {
    return snapshots.get(key)!
  }

  return { getSlice, setSlice, subscribe, getSnapshot }
}
